import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { Menu, MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MenuItem, MessageService } from 'primeng/api';
import { ProjectStateService } from '../../services/project-state.service';
import { ProjectComponent } from '../project/project';
import { AssignTemplate } from '../assign-template/assign-template';
import { AssignUsers } from '../assign-users/assign-users';
import { ProjectUsersModalComponent } from '../project-users-modal/project-users-modal';
import { ProjectService } from '../../services/project.service';
import { ProjectModel } from '../../models/project.model';
import { Toast } from 'primeng/toast';
import { AuthService } from '../../core/services/auth.service';
import { SSOService } from '../../services/sso.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SignalRService, BackupProgressEvent } from '../../services/signalr.service';
import { TemplateService } from '../../services/template.service';
import { BusinessUnitModel } from '../../models/template.model';
import { AppFeatureStateService } from '../../services/app-feature-state.service';
import { PROJECTS_FEATURE_KEYS } from '../../constants/app-menu.constants';
import { EffectiveSkidConfigurationService } from '../../services/effective-skid-configuration.service';
import { getProjectSkidConfigurationParts, SkidConfigurationParts } from '../../utils/skid-configuration.utils';

@Component({
  selector: 'app-home',
  imports: [CommonModule, MenuModule, ButtonModule, InputTextModule, FormsModule, Toast, TooltipModule, SelectModule],
  templateUrl: './home.html',
  styleUrl: './home.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Home implements OnInit, OnDestroy {
  projectList: ProjectModel[] = [];
  myProjects: ProjectModel[] = [];
  archivedProjects: ProjectModel[] = [];
  filteredProjectList: ProjectModel[] = [];
  filteredMyProjects: ProjectModel[] = [];
  filteredArchivedProjects: ProjectModel[] = [];
  projectMenuItems: MenuItem[] = [];
  currentMenuProjectId: number | null = null;
  ddRef: DynamicDialogRef | null = null;
  searchText: string = '';
  mySearchText: string = '';
  archivedSearchText: string = '';
  user: any = null;
  private userSubscription: any;
  private projectListRefreshSubscription: any;
  canSeeAllProjects = false;
  canSeeArchivedProjects = false;
  canCreateProject = false;
  activeTab: 'my' | 'all' | 'archived' = 'my';
  businessUnitOptions: { id: number; displayName: string }[] = [];
  selectedBusinessUnitId: number | null = null;
  backupStatusMap = new Map<number, BackupProgressEvent>();
  private effectiveSkidPartsByProjectId = new Map<number, SkidConfigurationParts>();
  private destroy$ = new Subject<void>();
  private projectsReload$ = new Subject<void>();
  private loadedTabs = new Set<'my' | 'all' | 'archived'>();
  private loadingTabs = new Set<'my' | 'all' | 'archived'>();
  private lastProjectLoadContext = '';

  @ViewChild('projectMenu') projectMenu!: Menu;

  // TrackBy function for better performance
  trackByProjectId = (index: number, item: ProjectModel): number | undefined => item.projectId;
  constructor(
    private router: Router,
    private dialogService: DialogService,
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private projectStateService: ProjectStateService,
    private authService: AuthService,
    private ssoService: SSOService,
    private templateService: TemplateService,
    private signalRService: SignalRService,
    private appFeatureState: AppFeatureStateService,
    private effectiveSkidConfiguration: EffectiveSkidConfigurationService
  ) { }

  ngOnInit(): void {
    console.log(this.authService.getCurrentUser());
    this.initializeMenuItems();
    this.initializeUser();
    this.loadBusinessUnits();
    this.subscribeProjectListRefresh();
    this.subscribeProjectSearch();
    // this.subscribeBackupProgress();
    // this.loadActiveBackupStatuses();
  }

  private initializeUser(): void {
    /*
     * RECOMMENDED PATTERN FOR ALL COMPONENTS:
     * 
     * 1. Subscribe to ssoService.user$ to get reactive updates
     * 2. GetUserByEmail is called automatically after SSO login (only once)
     * 3. All components get updated when API call completes
     * 4. Check for user.userId to ensure complete data is available
     * 5. Always clean up subscriptions in ngOnDestroy
     */
    
    // Subscribe to user changes to get updated data when GetUserByEmail completes
    this.userSubscription = this.ssoService.user$.subscribe(user => {
      this.user = user;
      console.log('User data updated in Home component:', this.user);

      this.canSeeAllProjects = this.canAccessAllProjects();
      this.canSeeArchivedProjects = this.canAccessArchivedProjects();
      this.canCreateProject = this.canCreateNewProject();
      this.ensureActiveTabAllowed();
      
      this.reloadProjectsForCurrentBusinessUnit();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.projectListRefreshSubscription) {
      this.projectListRefreshSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.projectsReload$.next();
    this.projectsReload$.complete();
  }

  private subscribeProjectListRefresh(): void {
    this.projectListRefreshSubscription = this.projectStateService.projectListRefresh$.subscribe(() => {
      this.reloadProjectsForCurrentBusinessUnit(true);
      this.cdr.markForCheck();
    });
  }

  private subscribeProjectSearch(): void {
    this.projectStateService.projectListSearch$
      .pipe(takeUntil(this.destroy$))
      .subscribe(searchText => {
        const value = (searchText || '').trim();
        this.searchText = value;
        this.mySearchText = value;
        this.archivedSearchText = value;
        this.filterMyProjects();
        this.filterProjects();
        this.filterArchivedProjects();
      });
  }

  // private subscribeBackupProgress(): void {
  //   this.signalRService.backupProgress$
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe(event => {
  //       if (!event) return;
  //       if (event.status === 'in_progress' || event.status === 'pending') {
  //         this.backupStatusMap.set(event.projectId, event);
  //       } else {
  //         this.backupStatusMap.delete(event.projectId);
  //       }
  //       this.cdr.markForCheck();
  //     });
  // }

  // private loadActiveBackupStatuses(): void {
  //   this.projectService.getActiveBackupStatuses().subscribe({
  //     next: (statuses) => {
  //       statuses.forEach(s => this.backupStatusMap.set(s.projectId, s));
  //       this.cdr.markForCheck();
  //     },
  //     error: () => { /* silently ignore */ }
  //   });
  // }

  // isBackupInProgress(projectId: number): boolean {
  //   const s = this.backupStatusMap.get(projectId);
  //   return s?.status === 'in_progress' || s?.status === 'pending';
  // }

  // getBackupTooltip(projectId: number): string {
  //   const s = this.backupStatusMap.get(projectId);
  //   if (!s) return '';
  //   if (s.status === 'in_progress' || s.status === 'pending')
  //     return `Backup in progress — ${s.filesProcessed}/${s.totalFiles} files`;
  //   if (s.status === 'completed') return 'Backup complete';
  //   if (s.status === 'failed') return 'Backup failed — contact administrator';
  //   return '';
  // }

  // getBackupIconClass(projectId: number): string {
  //   const s = this.backupStatusMap.get(projectId);
  //   if (!s) return '';
  //   if (s.status === 'in_progress' || s.status === 'pending') return 'ri-hourglass-fill backup-icon--progress';
  //   if (s.status === 'completed') return 'ri-checkbox-circle-fill backup-icon--done';
  //   if (s.status === 'failed') return 'ri-error-warning-fill backup-icon--error';
  //   return '';
  // }

  private initializeMenuItems(): void {
    // Default menu items, but projectId will be set per card
    this.projectMenuItems = [];
    this.setMenuForProject(0);
  }

  private loadBusinessUnits(): void {
    this.templateService.getBusinessUnits().subscribe({
      next: (units) => {
        const mapped = (units || []).map((u: BusinessUnitModel) => ({
          id: u.id,
          displayName: u.displayName || u.name
        }));
        this.businessUnitOptions = mapped;
        if (this.businessUnitOptions.length > 0) {
          const persistedBusinessUnitId = this.projectStateService.getProjectListBusinessUnitId();
          const persistedUnit = this.businessUnitOptions.find(x => x.id === persistedBusinessUnitId);
          const defaultUnit = this.businessUnitOptions.find(x => x.id === 1);
          this.selectedBusinessUnitId = persistedUnit
            ? persistedUnit.id
            : (defaultUnit ? defaultUnit.id : this.businessUnitOptions[0].id);
          this.projectStateService.setProjectListBusinessUnitId(this.selectedBusinessUnitId);
          this.reloadProjectsForCurrentBusinessUnit();
        } else {
          this.selectedBusinessUnitId = null;
          this.projectStateService.setProjectListBusinessUnitId(null);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.businessUnitOptions = [];
        this.selectedBusinessUnitId = null;
        this.projectStateService.setProjectListBusinessUnitId(null);
        this.cdr.markForCheck();
      }
    });
  }

  onBusinessUnitFilterChange(): void {
    this.projectStateService.setProjectListBusinessUnitId(this.selectedBusinessUnitId);
    this.reloadProjectsForCurrentBusinessUnit(true);
  }

  private matchesBusinessUnit(project: ProjectModel): boolean {
    if (this.selectedBusinessUnitId == null) return false;
    return project.businessUnitId === this.selectedBusinessUnitId;
  }

  private reloadProjectsForCurrentBusinessUnit(force = false): void {
    // Do not load projects until BU is selected and user context is available.
    if (this.selectedBusinessUnitId == null) {
      this.myProjects = [];
      this.filteredMyProjects = [];
      this.projectList = [];
      this.filteredProjectList = [];
      this.archivedProjects = [];
      this.filteredArchivedProjects = [];
      this.loadedTabs.clear();
      this.loadingTabs.clear();
      this.lastProjectLoadContext = '';
      return;
    }

    const loadContext = [
      this.user?.userId ?? '',
      this.selectedBusinessUnitId,
      this.canSeeAllProjects,
      this.canSeeArchivedProjects
    ].join('|');

    if (!force && loadContext === this.lastProjectLoadContext) {
      return;
    }

    this.projectsReload$.next();
    this.effectiveSkidPartsByProjectId.clear();
    this.effectiveSkidConfiguration.clear();
    this.lastProjectLoadContext = loadContext;
    this.loadedTabs.clear();
    this.loadingTabs.clear();
    this.myProjects = [];
    this.filteredMyProjects = [];
    this.projectList = [];
    this.filteredProjectList = [];
    this.archivedProjects = [];
    this.filteredArchivedProjects = [];

    console.log('Loading project tab:', this.activeTab, 'for user ID:', this.user?.userId, 'and BU:', this.selectedBusinessUnitId);
    // My Projects is also used to remove the user's assignments from All Projects.
    // All and Archived remain lazy until their tab is selected.
    this.ensureTabLoaded('my');
    if (this.activeTab !== 'my') {
      this.ensureTabLoaded(this.activeTab);
    }
  }

  private ensureTabLoaded(tab: 'my' | 'all' | 'archived'): void {
    if (this.loadedTabs.has(tab) || this.loadingTabs.has(tab)) {
      return;
    }

    if (tab === 'my' && !this.user?.userId) {
      return;
    }
    if (tab === 'all' && !this.canSeeAllProjects) {
      return;
    }
    if (tab === 'archived' && !this.canSeeArchivedProjects) {
      return;
    }

    this.loadingTabs.add(tab);
    if (tab === 'my') {
      this.loadMyProjects();
    } else if (tab === 'all') {
      this.loadAllProjects();
    } else {
      this.loadArchivedProjects();
    }
  }

  private completeTabLoad(tab: 'my' | 'all' | 'archived', succeeded: boolean): void {
    this.loadingTabs.delete(tab);
    if (succeeded) {
      this.loadedTabs.add(tab);
    }
  }

  canShowProjectActionMenu(project: ProjectModel): boolean {
    return this.getProjectMenuItems(project.projectId ?? 0).length > 0;
  }

  getProjectMenuItems(projectId: number): MenuItem[] {
    const project = this.findProjectById(projectId);
    if (!project) {
      return [];
    }

    const isActive = project?.isActive ?? true;
    const items: MenuItem[] = [];

    if (this.canEditProject()) {
      items.push({ label: 'Edit', icon: 'ri-pencil-line', command: () => this.showProjectDialog(projectId) });
    }

    if (this.canAssignTemplates()) {
      items.push({ label: 'Assign Template', icon: 'ri-folder-add-line', command: () => this.showAssignTemplateDialog(projectId) });
    }

    if (this.canManageProjectUsers()) {
      items.push({ label: 'Users', icon: 'ri-group-line', command: () => this.showAssignUsersDialog(projectId) });
    }

    if (this.canArchiveProjectAction()) {
      items.push({
        label: isActive ? 'Archive' : 'Un Archive',
        icon: 'ri-inbox-archive-line',
        command: () => this.archiveProject(projectId)
      });
    }

    return items;
  }

  private findProjectById(projectId: number): ProjectModel | undefined {
    return [...this.myProjects, ...this.projectList, ...this.archivedProjects].find(p => p.projectId === projectId);
  }

  private loadMyProjects(): void {
    // Assuming you have a method to get the current user ID
    const currentUserId = this.user?.userId;
    const businessUnitId = this.selectedBusinessUnitId ?? 1;

    this.projectService.getProjectListByUser(currentUserId, businessUnitId)
      .pipe(takeUntil(this.destroy$), takeUntil(this.projectsReload$))
      .subscribe({
      next: (projects) => {
        // Ensure data is an array before assigning
        this.myProjects = Array.isArray(projects) ? projects : [];
        this.sortByFavourite(this.myProjects);
        this.filterMyProjects();
        this.loadEffectiveSkidConfigurations(this.myProjects);
        
        // Re-filter the general project list to exclude newly loaded myProjects
        this.projectList = this.excludeMyProjectsFromList(this.projectList);
        this.filterProjects();
        
        // Manually trigger change detection for OnPush strategy
        this.completeTabLoad('my', true);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading user-specific project list:', error);
        this.myProjects = [];
        this.filteredMyProjects = [];
        this.completeTabLoad('my', false);
        // Manually trigger change detection for OnPush strategy
        this.cdr.markForCheck();
      }
      });
  }

  /**
   * Helper method to exclude user's projects from the general project list
   */
  private excludeMyProjectsFromList(allProjects: ProjectModel[]): ProjectModel[] {
    if (!this.myProjects || this.myProjects.length === 0) {
      return allProjects;
    }
    
    const myProjectIds = this.myProjects.map(p => p.projectId).filter(id => id !== undefined);
    return allProjects.filter(project => 
      project.projectId && !myProjectIds.includes(project.projectId)
    );
  }

  private loadAllProjects(): void {
    this.projectService.getProjectList(this.selectedBusinessUnitId ?? 1)
      .pipe(takeUntil(this.destroy$), takeUntil(this.projectsReload$))
      .subscribe({
      next: (data) => {
        // Ensure data is an array before assigning
        const allProjects = Array.isArray(data) ? data : [];
        
        // Exclude projects that are already in user's myProjects list
        this.projectList = this.excludeMyProjectsFromList(allProjects);
        this.filterProjects();
        this.loadEffectiveSkidConfigurations(this.projectList);

        // Manually trigger change detection for OnPush strategy
        this.completeTabLoad('all', true);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading project list:', error);
      this.projectList = [];
      this.filteredProjectList = [];
        this.completeTabLoad('all', false);
        // Manually trigger change detection for OnPush strategy
        this.cdr.markForCheck();
      }
      });
  }

  filterProjects(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      // Show all projects excluding user's myProjects
      this.filteredProjectList = this.projectList.filter(project => this.matchesBusinessUnit(project));
    } else {
      const searchTerm = this.searchText.toLowerCase().trim();
      // Filter projects by searching across all fields (Name, EQ#, CX#, WR#)
      this.filteredProjectList = this.projectList.filter(project =>
        this.matchesBusinessUnit(project) && (
          project.projectName?.toLowerCase().includes(searchTerm) ||
          project.projectNumber?.toLowerCase().includes(searchTerm) ||
          project.commProject?.toLowerCase().includes(searchTerm) ||
          project.warrantyProject?.toLowerCase().includes(searchTerm)
        )
      );
    }
    this.cdr.markForCheck();
  }

  filterMyProjects(): void {
    if (!this.mySearchText || this.mySearchText.trim() === '') {
      // Show all user's projects
      this.filteredMyProjects = this.myProjects.filter(project => this.matchesBusinessUnit(project));
    } else {
      const searchTerm = this.mySearchText.toLowerCase().trim();
      // Filter projects by searching across all fields (Name, EQ#, CX#, WR#)
      this.filteredMyProjects = this.myProjects.filter(project =>
        this.matchesBusinessUnit(project) && (
          project.projectName?.toLowerCase().includes(searchTerm) ||
          project.projectNumber?.toLowerCase().includes(searchTerm) ||
          project.commProject?.toLowerCase().includes(searchTerm) ||
          project.warrantyProject?.toLowerCase().includes(searchTerm)
        )
      );
    }
    this.cdr.markForCheck();
  }

  clearSearch(): void {
    this.searchText = '';
    this.filterProjects();
  }

  clearMySearch(): void {
    this.mySearchText = '';
    this.filterMyProjects();
  }

  setActiveTab(tab: 'my' | 'all' | 'archived'): void {
    if (tab === 'all' && !this.canSeeAllProjects) {
      return;
    }
    if (tab === 'archived' && !this.canSeeArchivedProjects) {
      return;
    }
    this.activeTab = tab;
    this.ensureTabLoaded(tab);
    this.cdr.markForCheck();
  }

  getProjectDetailLine(project: ProjectModel): string {
    const skidParts = this.getEffectiveSkidParts(project);

    const hasSkidValues = skidParts.some(v => v > 0);
    const hasTransformer = !!(project.transformerName && project.transformerName.trim());
    const hasIntegrator = !!(project.integratorName && project.integratorName.trim());
    const hasAux = !!(project.auxPackage && project.auxPackage.toString().trim());
    const hasOil = !!(project.oilContainment && project.oilContainment.toString().trim());

    // If nothing meaningful is present, suppress the second line entirely.
    if (!hasSkidValues && !hasTransformer && !hasIntegrator && !hasAux && !hasOil) {
      return '';
    }

    const segments: string[] = [];
    if (hasTransformer) segments.push(project.transformerName!.trim());
    if (hasIntegrator) segments.push(project.integratorName!.trim());
    if (hasSkidValues) {
      const skidLabel = skidParts
        .map((count, i) => ({ n: i + 1, count }))
        .filter(x => x.count > 0)
        .map(x => `N${x.n}:${x.count}`)
        .join(' | ');
      segments.push(skidLabel);
    }
    if (hasAux) segments.push(project.auxPackage!.toString().trim());
    if (hasOil) segments.push(project.oilContainment!.toString().trim());

    return segments.join('|');
  }

  getProjectStatusIndicatorClass(project: ProjectModel): string {
    const indicator = (project.projectStatusIndicator ?? '').trim().toLowerCase();
    if (indicator === 'green') {
      return 'project-status-indicator--green';
    }
    if (indicator === 'yellow') {
      return 'project-status-indicator--yellow';
    }
    return '';
  }

  getProjectStatusLineClass(project: ProjectModel): string {
    const indicator = (project.projectStatusIndicator ?? '').trim().toLowerCase();
    if (indicator === 'green') {
      return 'project-card--status-green';
    }
    if (indicator === 'yellow') {
      return 'project-card--status-yellow';
    }
    return '';
  }

  getProjectStatusIndicatorLabel(project: ProjectModel): string {
    const indicator = (project.projectStatusIndicator ?? '').trim().toLowerCase();
    if (indicator === 'green') {
      return 'Daily report submitted in the last 4 days';
    }
    if (indicator === 'yellow') {
      return 'Active resources on project; no daily report in the last 4 days';
    }
    return '';
  }

  private loadArchivedProjects(): void {
    this.projectService.getArchivedProjectList(this.selectedBusinessUnitId ?? 1)
      .pipe(takeUntil(this.destroy$), takeUntil(this.projectsReload$))
      .subscribe({
      next: (data) => {
        // Ensure data is an array before assigning
        this.archivedProjects = Array.isArray(data) ? data : [];
        this.filterArchivedProjects();
        this.loadEffectiveSkidConfigurations(this.archivedProjects);

        // Manually trigger change detection for OnPush strategy
        this.completeTabLoad('archived', true);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading archived project list:', error);
        this.archivedProjects = [];
        this.filteredArchivedProjects = [];
        this.completeTabLoad('archived', false);
        // Manually trigger change detection for OnPush strategy
        this.cdr.markForCheck();
      }
      });
  }

  filterArchivedProjects(): void {
    if (!this.archivedSearchText || this.archivedSearchText.trim() === '') {
      // Show all archived projects
      this.filteredArchivedProjects = this.archivedProjects.filter(project => this.matchesBusinessUnit(project));
    } else {
      const searchTerm = this.archivedSearchText.toLowerCase().trim();
      // Filter archived projects by searching across all fields (Name, EQ#, CX#, WR#)
      this.filteredArchivedProjects = this.archivedProjects.filter(project =>
        this.matchesBusinessUnit(project) && (
          project.projectName?.toLowerCase().includes(searchTerm) ||
          project.projectNumber?.toLowerCase().includes(searchTerm) ||
          project.commProject?.toLowerCase().includes(searchTerm) ||
          project.warrantyProject?.toLowerCase().includes(searchTerm)
        )
      );
    }
    this.cdr.markForCheck();
  }

  clearArchivedSearch(): void {
    this.archivedSearchText = '';
    this.filterArchivedProjects();
  }

  private getEffectiveSkidParts(project: ProjectModel): SkidConfigurationParts {
    return this.effectiveSkidPartsByProjectId.get(project.projectId ?? -1)
      ?? getProjectSkidConfigurationParts(project);
  }

  private loadEffectiveSkidConfigurations(projects: ProjectModel[]): void {
    for (const project of projects) {
      if (!project.projectId || this.effectiveSkidPartsByProjectId.has(project.projectId)) {
        continue;
      }

      this.effectiveSkidConfiguration.getEffectiveParts(project)
        .pipe(takeUntil(this.destroy$))
        .subscribe(parts => {
          if (!project.projectId) {
            return;
          }

          this.effectiveSkidPartsByProjectId.set(project.projectId, parts);
          this.cdr.markForCheck();
        });
    }
  }

  navigateToProject(project: ProjectModel): void {
    if (!project.projectId) {
      return;
    }

    // Set the project in the state service before navigation
    this.projectStateService.setCurrentProject(project);

    // Always fetch permissions on tile click, then navigate
    this.projectService.getUserProjectPermissions(project.projectId).subscribe({
      next: (permissions) => {
        this.projectStateService.setCurrentProjectPermissions(permissions);
        this.router.navigate(['/dashboard', project.projectId]);
      },
      error: () => {
        this.projectStateService.setCurrentProjectPermissions(null);
        this.router.navigate(['/dashboard', project.projectId]);
      }
    });
  }

  setMenuForProject(projectId: number) {
    this.projectMenuItems = this.getProjectMenuItems(projectId);
    this.currentMenuProjectId = projectId;
  }

  openProjectMenu(event: MouseEvent, projectId: number): void {
    event.stopPropagation();
    const project = this.findProjectById(projectId);
    if (!project || !this.canShowProjectActionMenu(project)) {
      return;
    }
    const target = event.currentTarget as HTMLElement;
    this.setMenuForProject(projectId);
    this.projectMenu.hide();
    setTimeout(() => {
      this.projectMenu.show({ currentTarget: target });
      setTimeout(() => this.repositionMenu(target));
    });
  }

  private repositionMenu(target: HTMLElement): void {
    const container = this.projectMenu.container;
    if (!container) return;

    const rect = target.getBoundingClientRect();
    const menuWidth = container.offsetWidth;
    const menuHeight = container.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = rect.bottom;
    let left = rect.left;

    if (top + menuHeight > vh) top = rect.top - menuHeight;
    if (left + menuWidth > vw) left = rect.right - menuWidth;
    if (left < 0) left = 0;
    if (top < 0) top = 0;

    container.style.position = 'fixed';
    container.style.top = `${top}px`;
    container.style.left = `${left}px`;
  }

  showProjectDialog(projectId: number): void {
    if (projectId > 0 && !this.canEditProject()) {
      return;
    }

    if (projectId <= 0 && !this.canCreateNewProject()) {
      return;
    }

    this.ddRef = this.dialogService.open(ProjectComponent, {
      header: projectId > 0 ? 'Edit Project' : 'Create New Project',
      closable: true,
      width: '50rem',
      height: '85vh',
      modal: true,
      maximizable: true,
      styleClass: 'kansei-dialog project-dialog',
      contentStyle: {
        overflow: 'hidden',
        padding: '0'
      },
      data: { projectId: projectId ?? 0 }
    });

    this.ddRef?.onClose.subscribe({
      next: (result) => {
        if (result) {
          console.log('Dialog closed with result:', result);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Project saved successfully' });
          // Reload the project list to show the new project
          this.loadAllProjects();
        }
      },
      error: (error) => {
        console.error('Error in dialog:', error);
      }
    });
  }

  showAssignTemplateDialog(projectId: number): void {
    if (!this.canAssignTemplates()) {
      return;
    }

    const projectName = this.findProjectById(projectId)?.projectName ?? '';
    this.ddRef = this.dialogService.open(AssignTemplate, {
      header: projectName ? `${projectName} - Assign Templates` : 'Assign Templates',
      closable: true,
      width: '40rem',
      modal: true,
      maximizable: false,
      styleClass: 'kansei-dialog',
      contentStyle: {
        overflow: 'auto'
      },
      data: { projectId: projectId }
    });

    this.ddRef?.onClose.subscribe({
      next: (result) => {
        if (result) {
          console.log('Templates assigned successfully');
          // Success message is already shown by the AssignTemplate component
        }
      },
      error: (error) => {
        console.error('Error in assign template dialog:', error);
      }
    });
  }

  showAssignUsersDialog(projectId: number): void {
    if (!this.canManageProjectUsers()) {
      return;
    }

    const projectName = this.findProjectById(projectId)?.projectName ?? '';
    this.ddRef = this.dialogService.open(AssignUsers, {
      header: projectName ? `${projectName} - Assign Users` : 'Assign Users',
      closable: true,
      width: '45rem',
      modal: true,
      maximizable: false,
      styleClass: 'kansei-dialog',
      contentStyle: {
        overflow: 'visible',
        padding: '0'
      },
      data: { projectId: projectId }
    });

    this.ddRef?.onClose.subscribe({
      next: (result) => {
        if (result) {
          console.log('Users assigned successfully');
          // Success message is already shown by the AssignUsers component
        }
      },
      error: (error) => {
        console.error('Error in assign users dialog:', error);
      }
    });
  }

  archiveProject(projectId: number): void {
    if (!this.canArchiveProjectAction()) {
      return;
    }

    const project = this.findProjectById(projectId);
    if (!project) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project not found'
      });
      return;
    }

    const isActive = project.isActive ?? true;
    const action = isActive ? 'archive' : 'unarchive';
    const actionPast = isActive ? 'archived' : 'unarchived';

    this.projectService.archiveProject(projectId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Project ${actionPast} successfully`
        });
        // Reload all project lists to reflect the updated status
        this.loadMyProjects();
        this.loadAllProjects();
        this.loadArchivedProjects();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error(`Error ${action}ing project:`, error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${action} project`
        });
      }
    });
  }

  showProjectUsersModal(projectId: number, projectName: string): void {
    if (!projectId) {
      return;
    }

    this.ddRef = this.dialogService.open(ProjectUsersModalComponent, {
      header: `${projectName} - Project Users`,
      closable: true,
      width: '60rem',
      height: 'auto',
      modal: true,
      maximizable: true,
      styleClass: 'project-users-dialog',
      contentStyle: {
        overflow: 'auto',
        padding: '0.5rem 1rem 1rem'
      },
      data: { projectId: projectId }
    });
  }

  toggleFavourite(item: ProjectModel): void {
    if (!item.projectId || !this.user?.userId) return;

    this.projectService.toggleFavouriteProject(item.projectId, this.user.userId).subscribe({
      next: () => {
        item.isFavourite = !item.isFavourite;
        this.sortByFavourite(this.myProjects);
        this.filteredMyProjects = [...this.myProjects];
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error toggling favourite:', error);
      }
    });
  }

  private sortByFavourite(projects: ProjectModel[]): void {
    projects.sort((a, b) => {
      if (a.isFavourite === b.isFavourite) return 0;
      return a.isFavourite ? -1 : 1;
    });
  }

  private canAccessAllProjects(): boolean {
    return this.appFeatureState.canView(PROJECTS_FEATURE_KEYS.ALL_PROJECTS);
  }

  private canAccessArchivedProjects(): boolean {
    return this.appFeatureState.canView(PROJECTS_FEATURE_KEYS.ARCHIVED_PROJECTS);
  }

  private canCreateNewProject(): boolean {
    return this.appFeatureState.canView(PROJECTS_FEATURE_KEYS.NEW_PROJECT);
  }

  private canEditProject(): boolean {
    return this.appFeatureState.canView(PROJECTS_FEATURE_KEYS.EDIT);
  }

  private canAssignTemplates(): boolean {
    return this.appFeatureState.canView(PROJECTS_FEATURE_KEYS.ASSIGN_TEMPLATE);
  }

  private canManageProjectUsers(): boolean {
    return this.appFeatureState.canView(PROJECTS_FEATURE_KEYS.USERS);
  }

  private canArchiveProjectAction(): boolean {
    return this.appFeatureState.canView(PROJECTS_FEATURE_KEYS.ARCHIVE);
  }

  private ensureActiveTabAllowed(): void {
    if (this.activeTab === 'all' && !this.canSeeAllProjects) {
      this.activeTab = this.canSeeArchivedProjects ? 'archived' : 'my';
      return;
    }

    if (this.activeTab === 'archived' && !this.canSeeArchivedProjects) {
      this.activeTab = this.canSeeAllProjects ? 'all' : 'my';
    }
  }
}
